type FlagSnippetParams = {
  flagKey: string;
  websiteId: number | null;
  bucketOn: 'visitor' | 'customer_id' | null;
  /** The tracking server's origin, from config.TRACKING_URL. */
  trackingUrl: string;
};

/** The SDK call for a code flag: ask for it, then branch on On. The server-side snippet's style. */
export function flagSdkSnippet({ flagKey, websiteId, bucketOn }: FlagSnippetParams): string {
  const isCustomerId = bucketOn === 'customer_id';
  const visitorLines = isCustomerId
    ? ''
    : [
        '// The visitor ID from your session, or a new one. Store a new one and send it back',
        '// on every request: a different ID is a different visitor.',
        'const visitorId = session.visitorId ?? pertento.createVisitorId();',
        'session.visitorId = visitorId;',
        '',
      ].join('\n');
  const identity = isCustomerId ? '{ id: customer.id }' : '{ visitorId }';

  return `import { createPertento } from '@pertentoai/sdk';

const pertento = createPertento({
  websiteId: ${websiteId ?? '<website id>'},
  serverKey: process.env.PERTENTO_SERVER_KEY,
});
await pertento.ready();

${visitorLines}const decision = await pertento.getVariant('${flagKey}', ${identity});

if (decision.variantKey === 'on') {
  // On: the new path. decision.payload holds the On payload, if any.
} else {
  // Off: what you do today.
}`;
}

/**
 * Reading a code flag in the page's own code, once the Pertento snippet has loaded
 * (runtime-experiments/src/flag-decision.ts): whether it is on, and the side's payload. Rings
 * can match facts the page sets before the snippet, in window.PertentoAttributes.
 */
export function flagBrowserSnippet({ flagKey }: Pick<FlagSnippetParams, 'flagKey'>): string {
  return `// Optional, before the Pertento snippet: facts your rings can match.
window.PertentoAttributes = { staff: false, plan: 'pro' };

// Once the Pertento snippet has loaded:
if (window.Pertento.isOn('${flagKey}')) {
  // On: the new path.
}
const { on, payload } = window.Pertento.flag('${flagKey}'); // payload: the side's JSON`;
}

/** POST /sdk/decide by hand, for a stack without the SDK. */
export function flagCurlSnippet({ flagKey, bucketOn, trackingUrl }: FlagSnippetParams): string {
  const identity = bucketOn === 'customer_id' ? '"id": "<customer id>"' : '"visitorId": "<visitor id>"';
  return `curl -X POST ${trackingUrl}/sdk/decide \\
  -H "Authorization: Bearer $PERTENTO_SERVER_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{ "keys": ["${flagKey}"], ${identity}, "attributes": {} }'`;
}
